import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Popover } from 'primeng/popover';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { BdGrantsTabComponent } from '../shareables-overview/grants-tab/grants-tab.component';
import { BdRecentTabComponent } from '../shareables-overview/recent-tab/recent-tab.component';
import { BdScopeSidebarComponent } from '../shareables-overview/scope-sidebar/scope-sidebar.component';
import { BdShareablesTabComponent } from '../shareables-overview/shareables-tab/shareables-tab.component';
import {
  BdWorkspaceNavigator,
  provideMemoryWorkspaceNavigation,
} from '../shareables-overview/navigation/workspace-navigator';
import {
  FolderItem,
  SHAREABLE_LIST_PICK,
  SidebarNavigation,
} from '../shareables-overview/shareables-overview.models';
import { BdResourcePickerFilterComponent } from './resource-picker-filter.component';
import {
  PickerView,
  RESOURCE_PICKER_CRITERIA_EMPTY,
  ResourcePickerCriteria,
} from './resource-picker.models';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

export type ResourcePickerLayoutMode = 'wide' | 'narrow';

interface ActiveSection {
  readonly icon: string;
  readonly label: string;
}

/**
 * Width (of *this component*, not the viewport) below which the scope sidebar
 * collapses into a popover behind a toolbar button. Deliberately lower than the
 * workspace page's 1024: the picker lives in dialogs around 48rem wide, where a
 * 1024 threshold would permanently collapse the sidebar and take the two-pane
 * layout with it.
 */
const NARROW_BREAKPOINT = 640;

/**
 * Reusable, routerless resource picker — the macOS file-open panel for BinDoc
 * shareables: scope sidebar on the left, browsable content on the right, search
 * and filter on top, multi-select throughout. Hosts embed it in their own dialog
 * and keep the confirm/cancel footer (and therefore the single primary button);
 * this component only reports what is selected via {@link selectionChange}.
 *
 * The scopes are the workspace ones minus the recycle bin: "Kürzlich verwendet",
 * "Eigene Ressourcen" (with folder browsing), "Geteilt" and any company group.
 * Typing in the search field replaces the pane with a flat own+grants result
 * list — folder browsing has no name search, so searching *within* a folder is
 * not a thing the API offers.
 *
 * Selection is held here and handed back down to every tab as a controlled
 * input, which is what makes ticks survive walking into a folder, switching
 * scope or a refetch (rows are matched by type+id, never by object identity).
 *
 * The host MUST give this component a resolvable height: the tables size
 * themselves with `scrollHeight="flex"` and scroll internally, which needs an
 * ancestor chain with a real height (in a dialog, e.g. a `height: 60vh` wrapper
 * — see the Klia attach dialog). Without one the tables grow to their content
 * and the dialog does the scrolling.
 */
@Component({
  selector: 'bd-resource-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The embedded tabs inject DialogService even with their row actions hidden.
  providers: [
    BD_DIALOG_SERVICE_PROVIDER,
    ...provideMemoryWorkspaceNavigation(),
  ],
  template: `
    <div
      class="bd-resource-picker flex flex-col flex-1 min-h-0 h-full"
      [attr.data-layout]="layoutMode()"
    >
      <div
        class="flex items-center gap-2 p-2 border-b border-[var(--p-content-border-color)]"
      >
        @if (layoutMode() === 'narrow') {
          <p-button
            severity="secondary"
            [text]="true"
            [icon]="activeSection().icon"
            iconPos="left"
            [label]="activeSection().label"
            styleClass="max-w-full"
            ariaLabel="Bereich wechseln"
            title="Bereich wechseln"
            [pt]="{ root: { 'data-test': dataTestPrefix() + '-scope-toggle' } }"
            (onClick)="navPopover()?.toggle($event)"
          >
            <i class="pi pi-chevron-down ml-2 text-xs opacity-70"></i>
          </p-button>
        }
        <p-inputgroup class="flex-1 min-w-0">
          <p-iconfield iconPosition="left">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <!--
              Uncontrolled on purpose: writing a debounced signal back into the
              field is how typed input gets clobbered. Clearing it (Escape, or a
              scope change while a search is active) therefore goes through the
              element reference — see clearSearch().
            -->
            <input
              #searchInput
              pInputText
              type="text"
              role="searchbox"
              fluid
              placeholder="Nach Name suchen…"
              aria-label="Nach Name suchen"
              [attr.data-test]="dataTestPrefix() + '-search'"
              (input)="onSearchInput($event)"
              (keydown.escape)="clearSearch()"
            />
          </p-iconfield>
          <p-inputgroup-addon>
            <p-button
              icon="pi pi-sliders-h"
              severity="secondary"
              [text]="true"
              [badge]="
                filter.activeCriteriaCount() > 0
                  ? filter.activeCriteriaCount().toString()
                  : undefined
              "
              badgeSeverity="contrast"
              ariaLabel="Suchfilter"
              title="Suchfilter"
              [pt]="{
                root: { 'data-test': dataTestPrefix() + '-filter-toggle' },
              }"
              (onClick)="filter.toggle($event)"
            />
          </p-inputgroup-addon>
        </p-inputgroup>
      </div>

      <bd-resource-picker-filter
        #filter
        [dataTestPrefix]="dataTestPrefix()"
        [allowedTypes]="shareableTypes()"
        [criteria]="criteria()"
        (criteriaChange)="criteria.set($event)"
      ></bd-resource-picker-filter>

      @if (layoutMode() === 'narrow') {
        <p-popover
          #navPopoverRef
          [pt]="{ content: { style: 'padding: 0.25rem' } }"
        >
          <bd-scope-sidebar
            [bordered]="false"
            maxHeight="70vh"
            [showRecycleBin]="false"
            [activeTab]="sidebarActiveTab()"
            [activeGroupId]="sidebarActiveGroupId()"
            (navigate)="onSidebarNavigate($event)"
          ></bd-scope-sidebar>
        </p-popover>
      }

      <div class="flex flex-1 min-h-0 overflow-hidden">
        @if (layoutMode() === 'wide') {
          <bd-scope-sidebar
            [showRecycleBin]="false"
            [activeTab]="sidebarActiveTab()"
            [activeGroupId]="sidebarActiveGroupId()"
            (navigate)="onSidebarNavigate($event)"
          ></bd-scope-sidebar>
        }
        <div
          class="bd-resource-picker__content flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden p-4"
        >
          @if (searchQuery()) {
            <!--
              Search view: the flat own+grants result list (server-side name
              query for own resources, client-side for grants). It replaces the
              active scope rather than filtering it — the folder API has no name
              search.
            -->
            <p class="flex-none text-xs font-medium text-muted-color mb-2">
              Suchergebnisse aus allen Bereichen
            </p>
            <bd-recent-tab
              class="flex flex-col flex-auto min-h-0"
              [searchQuery]="searchQuery()"
              [shareableTypes]="effectiveTypes()"
              [showType]="true"
              [presentation]="pickPresentation"
              [selection]="selection()"
              [pageSize]="pageSize()"
              (selectionChange)="onTabSelectionChange($event)"
            ></bd-recent-tab>
          } @else {
            @switch (activeView().tab) {
              @case ('recent') {
                <bd-recent-tab
                  class="flex flex-col flex-auto min-h-0"
                  [shareableTypes]="effectiveTypes()"
                  [showType]="true"
                  [presentation]="pickPresentation"
                  [selection]="selection()"
                  [pageSize]="pageSize()"
                  (selectionChange)="onTabSelectionChange($event)"
                ></bd-recent-tab>
              }
              @case ('shareables') {
                <bd-shareables-tab
                  class="flex flex-col flex-auto min-h-0"
                  [shareableTypes]="effectiveTypes()"
                  [showType]="true"
                  [presentation]="pickPresentation"
                  [selection]="selection()"
                  (selectionChange)="onTabSelectionChange($event)"
                ></bd-shareables-tab>
              }
              @case ('group') {
                <bd-shareables-tab
                  class="flex flex-col flex-auto min-h-0"
                  [shareableTypes]="effectiveTypes()"
                  [showType]="true"
                  [presentation]="pickPresentation"
                  [selection]="selection()"
                  (selectionChange)="onTabSelectionChange($event)"
                ></bd-shareables-tab>
              }
              @case ('grants') {
                <bd-grants-tab
                  class="flex flex-col flex-auto min-h-0"
                  [shareableTypes]="effectiveTypes()"
                  [showType]="true"
                  [presentation]="pickPresentation"
                  [selection]="selection()"
                  (selectionChange)="onTabSelectionChange($event)"
                ></bd-grants-tab>
              }
            }
          }
        </div>
      </div>
    </div>
  `,
  host: {
    class: 'flex flex-col h-full min-h-0',
  },
  styles: [
    // Layout is Tailwind on the host and elements; what stays here is the named
    // container (a container query needs one) and the ::ng-deep rules reaching
    // into PrimeNG's DOM. The folder tables collapse their own columns off their
    // own width, so no column rules are needed here.
    `
      .bd-resource-picker {
        container-type: inline-size;
        container-name: bd-resource-picker;
      }
      @container bd-resource-picker (max-width: 639px) {
        .bd-resource-picker__content {
          padding: 0.75rem;
        }
      }
      /* Let the search field fill the group next to the filter addon. */
      :host ::ng-deep .p-inputgroup > p-iconfield {
        flex: 1 1 auto;
        min-width: 0;
      }
      :host ::ng-deep .p-inputgroup > p-iconfield .p-iconfield {
        width: 100%;
      }
      /* Inside an input group, PrimeNG lifts a *focused* input to z-index 1 so
         its focus ring paints above the adjacent addons. The input is a later
         DOM sibling of the p-inputicon (also z-index 1), so at equal z the
         focused input paints over the magnifier and it vanishes while typing.
         Lift the icon one step above the focused input. */
      :host ::ng-deep .p-inputgroup p-inputicon {
        z-index: 2;
      }
    `,
  ],
  imports: [
    BdGrantsTabComponent,
    BdRecentTabComponent,
    BdResourcePickerFilterComponent,
    BdScopeSidebarComponent,
    BdShareablesTabComponent,
    Button,
    IconField,
    InputGroup,
    InputGroupAddon,
    InputIcon,
    InputText,
    Popover,
  ],
})
export class BdResourcePickerComponent {
  /** Restricts what is listed and pickable; `undefined` = all types. */
  public readonly shareableTypes = input<readonly ShareableType[] | undefined>(
    undefined,
  );
  /** Rows per page in the list views; `undefined` shows them without a pager. */
  public readonly pageSize = input<number | undefined>(10);
  /**
   * Prefix for this instance's `data-test` ids: `${prefix}-search`,
   * `${prefix}-filter-toggle`, `${prefix}-scope-toggle` (plus the filter
   * panel's `${prefix}-types` / `${prefix}-reset`).
   */
  public readonly dataTestPrefix = input('bd-resource-picker');

  /**
   * Every scope is shown in picking mode: checkboxes on files, no row actions,
   * no permissions column, no label filter. A constant, so the tabs' input
   * identity never changes between renders.
   */
  protected readonly pickPresentation = SHAREABLE_LIST_PICK;

  /**
   * The complete current selection, re-emitted on every change — hosts map it
   * to their own model and count it for their confirm button.
   */
  public readonly selectionChange = output<readonly FolderItem[]>();

  /**
   * The picker browses with the workspace's navigation, kept in memory: the
   * page's URL belongs to the page the picker was opened from.
   */
  private readonly _navigator = inject(BdWorkspaceNavigator);

  /** Scope shown in the content pane. */
  public readonly activeView = computed<PickerView>(() => {
    const location = this._navigator.location();
    switch (location.tab) {
      case 'group':
        return {
          tab: 'group',
          groupId: location.groupId,
          groupName: this._navigator.names().groupName,
        };
      case 'recycle-bin':
        // Not offered by the picker's sidebar; archived items are not pickable.
        return { tab: 'recent' };
      default:
        return { tab: location.tab };
    }
  });

  /**
   * Master selection store. Every tab renders it as its controlled `selection`
   * and reports back a *complete* selection (the folder table merges in the
   * picks that are not part of the list it shows), so this is a plain overwrite.
   */
  public readonly selection = signal<readonly FolderItem[]>([]);

  /** Debounced name query; non-empty switches the pane to the search view. */
  public readonly searchQuery = signal<string | undefined>(undefined);

  /** Criteria of the filter popover (owned here, edited by the child). */
  public readonly criteria = signal<ResourcePickerCriteria>({
    ...RESOURCE_PICKER_CRITERIA_EMPTY,
  });

  /**
   * Types handed to the tabs: the user's narrowing when set, else the host's
   * allowed types. An empty facet must never *widen* past what the host allows.
   */
  public readonly effectiveTypes = computed<
    readonly ShareableType[] | undefined
  >(() => {
    const picked = this.criteria().types;
    return picked.length > 0 ? picked : this.shareableTypes();
  });

  public readonly layoutMode = signal<ResourcePickerLayoutMode>('wide');

  /**
   * While a search is active the content pane shows a flat, all-scopes result
   * set rather than the active scope, so no sidebar entry should read as
   * "applied" — both go `undefined` for the duration of the search.
   */
  protected readonly sidebarActiveTab = computed<PickerView['tab'] | undefined>(
    () => (this.searchQuery() ? undefined : this.activeView().tab),
  );
  protected readonly sidebarActiveGroupId = computed<string | undefined>(() =>
    this.searchQuery() ? undefined : this.activeView().groupId,
  );

  /**
   * Icon + label of the current scope, shown on the narrow-mode scope button.
   * Always reflects the underlying scope (not {@link sidebarActiveTab}) — even
   * mid-search this is the "go browse somewhere" affordance, and picking an
   * entry from it clears the search anyway.
   */
  public readonly activeSection = computed<ActiveSection>(() => {
    const view = this.activeView();
    switch (view.tab) {
      case 'recent':
        return { icon: 'pi pi-clock', label: 'Kürzlich verwendet' };
      case 'shareables':
        return { icon: 'pi pi-user', label: 'Eigene Ressourcen' };
      case 'grants':
        return { icon: 'pi pi-share-alt', label: 'Geteilt' };
      case 'group':
        return { icon: 'pi pi-users', label: view.groupName ?? 'Gruppe' };
      default:
        return { icon: 'pi pi-bars', label: 'Bereich' };
    }
  });

  public readonly navPopover = viewChild<Popover>('navPopoverRef');
  private readonly _searchInput =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _host = inject(ElementRef<HTMLElement>);
  private readonly _search$ = new Subject<string>();

  constructor() {
    this._navigator.connect('recent');

    // `distinctUntilChanged` sits *before* the debounce so that an imperative
    // clear (Escape, scope change) is seen by it: behind the debounce the empty
    // string would be swallowed by the next keystroke, and re-typing the term
    // that was just cleared would then be dropped as a duplicate.
    this._search$
      .pipe(distinctUntilChanged(), debounceTime(300), takeUntilDestroyed())
      .subscribe((query) => {
        const trimmed = query.trim();
        this.searchQuery.set(trimmed.length > 0 ? trimmed : undefined);
      });

    // The layout follows this component's own width, not the viewport's, so it
    // behaves the same in a dialog, a side panel or a full page.
    afterNextRender(() => {
      if (typeof ResizeObserver === 'undefined') {
        return;
      }
      const host: HTMLElement = this._host.nativeElement;
      const apply = (width: number): void => {
        const mode: ResourcePickerLayoutMode =
          width < NARROW_BREAKPOINT ? 'narrow' : 'wide';
        if (mode !== this.layoutMode()) {
          this.layoutMode.set(mode);
        }
      };
      apply(host.getBoundingClientRect().width);
      const observer = new ResizeObserver((entries) =>
        apply(entries[0]?.contentRect.width ?? 0),
      );
      observer.observe(host);
      this._destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  public onSearchInput(event: Event): void {
    this._search$.next((event.target as HTMLInputElement).value);
  }

  /**
   * Drops the query and the text in the (uncontrolled) field. The empty string
   * is pushed through the pipeline as well so `distinctUntilChanged` forgets the
   * cleared term — otherwise re-typing the same term would be swallowed.
   */
  public clearSearch(): void {
    const input = this._searchInput();
    if (input) {
      input.nativeElement.value = '';
    }
    this.searchQuery.set(undefined);
    this._search$.next('');
  }

  /**
   * A picked sidebar destination switches the scope — and ends an active search:
   * the user just said they want to browse instead.
   */
  public onSidebarNavigate(nav: SidebarNavigation): void {
    // The picker's sidebar hides the recycle bin, so this cannot arrive; guard
    // anyway rather than showing archived items nobody can pick.
    if (nav.tab === 'recycle-bin') {
      return;
    }
    if (nav.tab === 'group' && !nav.groupId) {
      return;
    }
    this.clearSearch();
    this._navigator.selectTab(nav.tab, nav.groupId, nav.groupName);
    this.navPopover()?.hide();
  }

  /** A tab reported a new (complete) selection — store it and pass it on. */
  public onTabSelectionChange(items: readonly FolderItem[]): void {
    this.selection.set(items);
    this.selectionChange.emit(items);
  }
}
